import { useParams } from 'react-router-dom'
import useActionGuard from 'hooks/useActionGuard'
import { useSelector } from 'react-redux'
import { userSlice } from '@wellesley/user'
import { Megaphone, Pin, PencilLine, Trash } from 'lucide-react'
import {
  usePostApiV1PostsPinsByPostIdMutation,
  useDeleteApiV1PostsPinsByPostIdMutation,
} from '@wellesley/openapi'

import { redButton, TFeedActionsMenu } from '../../types'

import useHistoryActions from '../useHistoryActions'
import { Action } from '../../../../../ActionsDropdown'

const iconProps = {
  size: 20,
  color: 'var(--gray-900)',
}

type Props = Omit<TFeedActionsMenu, 'renderEditModal'> & {
  isReply?: boolean
  onEdit: () => void
  onReport: () => void
  onDelete: () => void
  muteUnmuteAction: Action
  blockUnblockAction: Action
}

const useFeedActions = ({
  feed,
  isReply,
  isAuthor,
  canPinPost,
  onEdit,
  onReport,
  onDelete,
  muteUnmuteAction,
  blockUnblockAction,
  isVisiblePinAction,
}: Props) => {
  const params = useParams()
  const { isDisabled, guardedAction } = useActionGuard()

  const feedId = feed.id

  const [addPinPost] = usePostApiV1PostsPinsByPostIdMutation()
  const [removePinPost] = useDeleteApiV1PostsPinsByPostIdMutation()

  const handlePinPost = async () => {
    const params = { postId: feedId }
    if (feed.pinned) {
      await removePinPost(params)
    } else void addPinPost(params)
  }

  const isGuest = !useSelector(userSlice.selectors.selectCurrentUser)

  const isExternalUser = !isAuthor || isGuest

  const hideEditButton = isExternalUser

  const isUserPage = params?.username
  const hidePinButton =
    isExternalUser || !isUserPage || isReply || !isVisiblePinAction

  const [historyActions] = useHistoryActions({ feed })

  const actions = [
    muteUnmuteAction,
    blockUnblockAction,
    {
      icon: <PencilLine {...iconProps} />,
      name: 'Edit',
      isHidden: hideEditButton,
      handler: guardedAction(onEdit),
      disabled: isDisabled,
    },
    {
      icon: <Pin {...iconProps} />,
      name: feed.pinned ? 'Unpin post' : 'Pin post',
      isHidden: hidePinButton,
      tooltipText: canPinPost ? '' : 'Pinned posts limit reached',
      handler: guardedAction(() => void handlePinPost()),
      disabled: isDisabled || (!canPinPost && !feed.pinned),
    },
    ...historyActions,
    {
      icon: <Megaphone {...iconProps} color="var(--red-500)" />,
      name: 'Report',
      className: redButton,
      isHidden: isAuthor,
      handler: guardedAction(onReport),
      disabled: isDisabled,
    },
    {
      icon: <Trash {...iconProps} color="var(--red-500)" />,
      name: 'Delete',
      isHidden: isExternalUser,
      handler: guardedAction(onDelete),
      className: redButton,
      disabled: isDisabled,
    },
  ].filter((a) => a && !a.isHidden)

  return actions
}

export default useFeedActions
